// ═══════════════════════════════════════════════════════════════════════════
// Voice-profile extractor — the chat AI builds each creator's profile.
//
// At episode completion we hand the FULL conversation transcript (plus
// whatever the planner already inferred) to one model call and ask it
// to describe this specific person: how they talk, the words/slang they
// actually use, their cadence, their signature moves, who they're for,
// and — critically — anything they said to keep OUT of episodes.
//
// The structured result is merged into creator_voice_profiles via
// upsertVoiceProfile() (refine-not-overwrite: arrays append+dedupe so
// the profile gets sharper every episode; a contradicting register is
// preserved, not clobbered). Read back at the next conversation start.
//
// Nothing here is creator-specific. It derives everything per-user from
// their own words. Fully general across genres/formats. SHIP-SAFE: any
// failure → null → caller falls back to the thin completion capture, so
// this can only add signal, never regress.
// ═══════════════════════════════════════════════════════════════════════════

import { askClaude, parseLLMJson } from './landingChatEngine';
import { modelFor } from './modelConfig';

const MAX_ARR = 24;       // cap each list so the profile can't balloon
const MAX_STR = 280;      // cap each text field
const MAX_ITEM = 200;     // cap each array item

function clean(s, max = MAX_STR) {
  return String(s == null ? '' : s).replace(/\s+/g, ' ').trim().slice(0, max);
}

function arr(v) {
  let a = v;
  if (typeof a === 'string') { try { a = JSON.parse(a); } catch { a = [a]; } }
  if (!Array.isArray(a)) return [];
  const seen = new Set();
  const out = [];
  for (const it of a) {
    const s = clean(it, MAX_ITEM);
    const k = s.toLowerCase();
    if (s && !seen.has(k)) { seen.add(k); out.push(s); }
    if (out.length >= MAX_ARR) break;
  }
  return out;
}

// PURE — exported for headless testing. Turns whatever the model
// returned into a clean upsertVoiceProfile() payload, or null if there
// is essentially no signal (so we never overwrite a real profile with
// emptiness).
export function normalizeVoiceExtract(parsed) {
  if (!parsed || typeof parsed !== 'object') return null;
  const out = {};
  const reg = clean(parsed.register);
  const rhy = clean(parsed.rhythm);
  const aud = clean(parsed.audience);
  const as  = clean(parsed.audience_state);
  const pg  = clean(parsed.primary_genre, 60);
  const sg  = clean(parsed.sub_genre, 60);
  if (reg) out.register = reg;
  if (rhy) out.rhythm = rhy;
  if (aud) out.audience = aud;
  if (as)  out.audience_state = as;
  if (pg)  out.primary_genre = pg;
  if (sg)  out.sub_genre = sg;
  const vocab = arr(parsed.vocabulary);
  const moves = arr(parsed.signature_moves);
  const avoid = arr(parsed.avoid_list);
  if (vocab.length) out.vocabulary = vocab;
  if (moves.length) out.signature_moves = moves;
  if (avoid.length) out.avoid_list = avoid;
  return Object.keys(out).length ? out : null;
}

// PURE — exported for headless testing. Renders a returning-creator
// block for the chat user-prompt. Empty string when there's no usable
// profile, so a brand-new creator's chat is byte-for-byte unchanged.
export function voiceProfileChatBlock(profile) {
  if (!profile || typeof profile !== 'object') return '';
  const reg = clean(profile.register);
  const rhy = clean(profile.rhythm);
  const aud = clean(profile.audience);
  const vocab = arr(profile.vocabulary);
  const moves = arr(profile.signature_moves);
  const avoid = arr(profile.avoid_list);
  const epi = Number(profile.episodes_count) || 0;
  if (!reg && !rhy && !aud && !vocab.length && !moves.length && !avoid.length) return '';
  const L = [];
  L.push('═══════════════════════════════════════════════════════════════════════');
  L.push(`RETURNING CREATOR — you already know this person${epi ? ` (${epi} episode${epi === 1 ? '' : 's'} together)` : ''}`);
  L.push('═══════════════════════════════════════════════════════════════════════');
  if (reg)   L.push(`  HOW THEY SOUND: ${reg}`);
  if (rhy)   L.push(`  THEIR RHYTHM:   ${rhy}`);
  if (aud)   L.push(`  THEIR AUDIENCE: ${aud}`);
  if (vocab.length) L.push(`  WORDS THEY USE: ${vocab.slice(0, 16).join(' · ')}`);
  if (moves.length) L.push(`  SIGNATURE MOVES: ${moves.slice(0, 10).join(' · ')}`);
  L.push('  Write and talk in THIS register from the first message — do not make them re-explain who they are. Mirror their vocabulary and cadence. Keep refining it as they talk.');
  if (avoid.length) {
    L.push('');
    L.push('  ⚠ THEY PREVIOUSLY ASKED TO KEEP THESE OUT OF EPISODES:');
    avoid.forEach(a => L.push(`    ✕ ${a}`));
    L.push('  If the user brings up anything on this list again, do NOT silently include it and do NOT silently exclude it. ASK once, plainly: e.g. "Last time you wanted to keep [that] out of the episode — do you want to bring it in openly this time, or keep it out again?" Then honor their answer for THIS episode. (One ask, not every turn.)');
  }
  return L.join('\n');
}

// PURE — exported for headless testing. Renders the learned voice profile
// as a MANDATORY block for the EPISODE-GENERATION user prompt (episodeBrief),
// so a finished episode sounds like the creator, not a generic host. Distinct
// from voiceProfileChatBlock: framed for writing output, not conversation, and
// it honors the avoid_list as a hard exclusion (the builder isn't a dialogue,
// so it can't "ask" the way chat does). `memory` is the podcaster_profiles row
// (recurring_themes / topics_explored / communication_style). Empty string
// when there's no usable signal, so a brand-new creator's brief is unchanged.
export function voiceProfileEpisodeBlock(vp, memory) {
  const reg   = clean(vp?.register);
  const rhy   = clean(vp?.rhythm);
  const vocab = arr(vp?.vocabulary);
  const moves = arr(vp?.signature_moves);
  const avoid = arr(vp?.avoid_list);
  const style = clean(memory?.communication_style, 60);
  const themesRaw = arr(memory?.recurring_themes).concat(arr(memory?.topics_explored));
  const seen = new Set();
  const themes = [];
  for (const t of themesRaw) { const k = t.toLowerCase(); if (!seen.has(k)) { seen.add(k); themes.push(t); } }
  const epi = Number(vp?.episodes_count) || 0;
  if (!reg && !rhy && !style && !vocab.length && !moves.length && !avoid.length && !themes.length) return '';
  const L = [];
  L.push('CREATOR VOICE — MANDATORY. Write EVERY field (title, hook_alternates, script_or_points, questions) in this specific creator’s established voice, learned from their past episodes and conversations. This is a real person you already know, not a generic host.');
  if (epi) L.push(`(Refined across ${epi} episode${epi === 1 ? '' : 's'} with them; keep it consistent.)`);
  if (reg)   L.push(`How they sound: ${reg}`);
  if (rhy)   L.push(`Their rhythm: ${rhy}`);
  if (style) L.push(`Communication style: ${style}`);
  if (vocab.length) L.push(`Words and phrases they actually use (mirror these naturally, don’t force all of them): ${vocab.slice(0, 16).join(' · ')}`);
  if (moves.length) L.push(`Signature moves to echo where they fit: ${moves.slice(0, 10).join(' · ')}`);
  if (themes.length) L.push(`Themes they keep returning to (weave in only where they fit this topic): ${themes.slice(0, 10).join(' · ')}`);
  if (avoid.length) {
    L.push('Keep these OUT of the episode. The creator asked to avoid these boundaries; do not reference, imply, or build around them:');
    avoid.forEach(a => L.push(`  ✕ ${a}`));
  }
  return L.join('\n');
}

// The model call. Returns a normalized payload or null.
export async function extractVoiceProfile({ transcript, plan, captured, vibe }, { signal } = {}) {
  try {
    const t = clean(transcript, 8000);
    if (!t) return null;
    const planVoice = plan && plan.voice ? `\nPlanner already noticed: ${clean(plan.voice, 400)}` : '';
    const planExcl = plan && Array.isArray(plan.exclusions) && plan.exclusions.length
      ? `\nThings they said to keep out: ${plan.exclusions.map(e => clean(e, 120)).join('; ')}` : '';
    const genre = (vibe && (vibe.primary || vibe.id || vibe)) || (captured && captured.genre) || '';

    const prompt = `From the podcast-planning conversation below, build a VOICE PROFILE of this specific creator — how THEY talk, so future episodes sound like them and the chat already knows them next time. Derive everything from their own words. Output JSON only, no prose, no fences.

{
  "register": "one or two sentences: their tone, energy, persona — how they actually sound (e.g. 'calm, lived-experience wisdom; older-brother steady; helps rather than lectures')",
  "rhythm": "their sentence shape/cadence (e.g. 'short punchy declaratives, the occasional long reflective build')",
  "vocabulary": ["distinctive words, slang, and phrases THEY actually used in the conversation — real samples only, not generic filler"],
  "signature_moves": ["recurring devices in how they communicate — e.g. 'opens on a scenario', 'pivots to a blunt one-liner', 'asks the listener a question'"],
  "avoid_list": ["anything they said to keep OUT of episodes, keep private, or not reference — name the BOUNDARY, never the private detail itself (e.g. 'host's personal divorce/marriage story', 'no trendy slang')"],
  "audience": "who they're making this for, in their framing",
  "audience_state": "the emotional state/situation that audience is in",
  "primary_genre": "business | truecrime | society | comedy | education | interview (or a short plain label)",
  "sub_genre": "optional short label or empty"
}

Rules: only include what the conversation actually evidences — empty string / [] when unknown, never invent. The avoid_list must capture the boundary as an instruction, and must NEVER contain the private content itself.${planVoice}${planExcl}
GENRE HINT: ${genre || '(infer)'}

CONVERSATION:
"""${t}"""`;

    const raw = await askClaude(prompt, modelFor('generation'), { max_tokens: 1200, signal });
    return normalizeVoiceExtract(parseLLMJson(raw));
  } catch (e) {
    console.warn('[voiceProfileExtractor] extract failed (using thin fallback):', e?.message || e);
    return null;
  }
}
